import {
  Badge,
  CheckableAvatarSelectItem,
  DropdownSelect,
  Icon,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo } from 'react';

import { searchApi } from 'apis/search';
import { useConversationsPaginatedFilterSearch } from 'components/conversation/conversationFiltersToolbar';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

type ConversationTimelineDetailsRequesterFilterProps = {
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  onClear: () => void;
};

export const ConversationTimelineDetailsRequesterFilter = ({
  selectedIds,
  onChange,
  onClear,
}: ConversationTimelineDetailsRequesterFilterProps): JSX.Element => {
  const {
    arg,
    items: members,
    hasNextPage,
    changeArg,
    nextPage,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: {
      sort: {
        key: 'display_name',
      },
    },
  });
  const dropdownItems = useMemo(
    () =>
      members.map(({ id, display_name, email, phone_number }) => ({
        id,
        name: display_name || email || phone_number || id,
      })),
    [members],
  );

  const { handleSearch, handleToggle } = useConversationsPaginatedFilterSearch({
    arg,
    changeArg,
  });

  const handleChange = useCallback(
    (item, checked: boolean) => {
      if (checked) {
        onChange([...selectedIds, item.id]);
      } else {
        onChange(selectedIds.filter((ids) => ids !== item.id));
      }
    },
    [selectedIds, onChange],
  );

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow
      items={dropdownItems}
      selectedItems={selectedIds}
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
      }}
      disabledItems={selectedIds.length === 1 ? selectedIds : undefined}
      tooltipItems={
        selectedIds.length === 1
          ? {
              [selectedIds[0]]: {
                content: 'At least 1 requester should be selected',
              },
            }
          : {}
      }
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(nextPage()),
      }}
      onClearText="Reset"
      onChange={handleChange}
      onClear={onClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      <Tooltip
        content="Filter by requester"
        wrap="div"
        wrapProps={{
          style: { display: 'flex', gap: 'var(--dixa-size-spacer-100)' },
        }}
      >
        <Icon name="filter" />
        <Badge variant="secondary" size="small">
          {selectedIds.length}
        </Badge>
      </Tooltip>
    </DropdownSelect>
  );
};
